import React, { Fragment } from "react";
import { useTable, useFlexLayout } from "react-table";
import Tooltip from "./form-components/Tooltip.jsx";
import { fillColors } from "../constants.js";
import { COST_TABLE_MEASURES, FORECAST_TABLE_MEASURES, RESOURCE_CHANGES_TABLE_MEASURES, INSIGHT_TABLE_MEASURES, RESOURCE_LIST_TABLE_MEASURES, DEFAULT_TABLE_MEASURES, DEFAULT_RESOURCE_CHANGES_TABLE_MEASURES, DEFAULT_INSIGHT_TABLE_MEASURES, DEFAULT_RESOURCE_LIST_TABLE_MEASURES } from "./table/measureRegistry";



const AnalyzerTable = (props) => {
	const { sourceData, grouping, measures, endpoint, tableType, showIcons = false, showTotalRow = false, onRowClick } = props;

	const getColor = (index) => {
		return fillColors[index > fillColors.length ? fillColors.length : index];
	}
    
    // Derive data directly from sourceData to avoid state updates and infinite loops
    const data = React.useMemo(() => {
        const sourceDataArray = sourceData?.data;
        if (Array.isArray(sourceDataArray) && sourceDataArray.length > 0) {
            return sourceDataArray;
        }
        return [];
    }, [sourceData?.data]);

	// Derive table type from explicit props (endpoint or tableType) instead of probing measure ids
	// This prevents misclassification when measure ids collide (e.g., "change" exists in both registries)
	const isResourceChangesTable = endpoint === "/get-resource-changes" || tableType === "resource-changes";
	const isInsightTable = tableType === "insight" || tableType === "insighttable";
	const isResourceListTable = tableType === "resource-list";

	// Use the appropriate registry instead of merging all registries to avoid id collisions
	// Select the registry based on the explicit table type rather than inferring from measures
	const ALL_MEASURES = isResourceChangesTable 
		? RESOURCE_CHANGES_TABLE_MEASURES 
		: isInsightTable
			? INSIGHT_TABLE_MEASURES
			: isResourceListTable
				? RESOURCE_LIST_TABLE_MEASURES
				: { ...COST_TABLE_MEASURES, ...FORECAST_TABLE_MEASURES };

	const selectedMeasureIds = Array.isArray(measures) && measures.length > 0 
		? measures 
		: isResourceChangesTable 
			? DEFAULT_RESOURCE_CHANGES_TABLE_MEASURES 
			: isInsightTable
				? DEFAULT_INSIGHT_TABLE_MEASURES
				: isResourceListTable
					? DEFAULT_RESOURCE_LIST_TABLE_MEASURES
					: DEFAULT_TABLE_MEASURES;

	// Format grouping value: replace underscores with spaces and capitalize words
	const formatGroupingLabel = (groupingValue) => {
		if (!groupingValue) return 'Grouping';
		return groupingValue
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	};

	const groupingColumn = {
		id: "grouping",
		Header: (cell) => (
			<div>
				<span data-tooltip-id="instantTooltip" data-tooltip-header={formatGroupingLabel(grouping || sourceData?.grouping)} data-tooltip-content="The name of the cost">
					{formatGroupingLabel(grouping || sourceData?.grouping)}
				</span>
			</div>
		),
		accessor: "grouping",
		Cell: (row) => (
			<div className="mt-2 font-normal text-text-2 flex-row flex">
				<div className="flex-row flex items-center">
					{showIcons && (
						<svg xmlns="http://www.w3.org/2000/svg" width="20" height="34" className="">
							<rect x="0" y="12" width="8" height="8" fill={getColor(row.row.index)} />
						</svg>
					)}
					{row.value}
				</div>
			</div>
		),
		minWidth: 200,
		// No maxWidth or width - let it grow to fill available space
	};

	const measureColumns = selectedMeasureIds
		.map((id) => ALL_MEASURES[id])
		.filter(Boolean)
		.map((m, measureIndex) => {
			// For percentage_of_total, create a custom accessor that has access to all data
			let accessor = typeof m.accessor === 'function' ? m.accessor : m.id;
			if (m.id === 'percentage_of_total') {
				accessor = (row) => {
					// Calculate total from all data
					const total = data.reduce((sum, r) => {
						const value = r.current_period || 0;
						return sum + (typeof value === 'number' ? value : 0);
					}, 0);
					// Return the current value and total for use in cell function
					const currentValue = row.current_period || 0;
					return { currentValue, total };
				};
			}
			
			// Determine alignment based on measure className or default to right
			const isLeftAligned = m.className && m.className.includes("text-left");
			const alignmentClass = isLeftAligned ? "text-left" : "text-right";
			// First column (first measure) should not have pl-4
			const isFirstColumn = measureIndex === 0;
			const paddingClass = isFirstColumn 
				? (isLeftAligned ? '' : 'pr-4')
				: (isLeftAligned ? 'pl-4' : 'pr-4');
			
			return {
				id: m.id,
				Header: () => (
					<div className={`${alignmentClass} ${paddingClass} mr-0`}>
						{m.hideHeader ? (
							<span></span>
						) : (
							<span data-tooltip-id="instantTooltip" data-tooltip-header={m.label} data-tooltip-content={m.headerTooltip || m.label}>
								{m.label}
							</span>
						)}
					</div>
				),
				accessor: accessor,
				Cell: (row) => (
					<div className={[
						`${alignmentClass} mt-2 truncate`,
						m.className || "",
						m.dynamicClassName ? m.dynamicClassName(row.value) : ""
					].join(" ").trim()}>
						{m.cell(row)}
					</div>
				),
				minWidth: m.minWidth ?? 200,
				maxWidth: m.maxWidth ?? 200,
				width: m.maxWidth ?? m.minWidth ?? 200,
			};
		});

	// For insight tables, don't include the grouping column
	const columnDefs = isInsightTable ? measureColumns : [groupingColumn, ...measureColumns];

	// function determineColumns(payments) {
	// 	var columns = [];

	// 	if (payments.length > 0) {
	// 		for (let i = 0; i < Object.keys(payments[0]).length; i++) {
	// 			columns.push({
	// 				Header: Object.keys(payments[0])[i],
	// 				accessor: Object.keys(payments[0])[i],
	// 			});
	// 		}
	// 	}

	// 	return columns;
	// }

    // Show empty state if no data
    if (!data || data.length === 0) {
        const emptyMessage = isResourceChangesTable 
            ? "No resource changes found for the selected period."
            : "No data available";
        const emptyDescription = isResourceChangesTable
            ? ""
            : "Try adjusting your filters or date range";
        
        return (
            <Fragment>
                <Tooltip />
                <div className="h-full w-full flex flex-col min-h-0 items-center justify-center">
                    <div className="text-center py-8 px-4">
                        <div className="text-text-2 text-lg mb-2">
                            {emptyMessage}
                        </div>
                        <div className="text-text-2 text-sm">
                            {emptyDescription}
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }

    // Calculate totals for total row
    const calculateTotals = () => {
        if (!showTotalRow || !data || data.length === 0) return null;
        
        const totals = {};
        selectedMeasureIds.forEach((measureId) => {
            const measure = ALL_MEASURES[measureId];
            if (!measure) return;
            
            // Measures that can be summed (numeric values)
            const summableMeasures = [
                'current_period', 
                'previous_period', 
                'change', 
                'forecasted_cost', 
                'forecast_change',
                // Resource changes table measures
                'resource_change_total',
                'min_change',
                'plus_change',
                'cost',
                // Resource list table measures
                'units',
                // Insight table measures
                'costs'
            ];
            
            if (summableMeasures.includes(measureId)) {
                const sum = data.reduce((acc, row) => {
                    // If measure has an accessor function, use it to get the value
                    let value;
                    if (typeof measure.accessor === 'function') {
                        value = measure.accessor(row);
                    } else {
                        value = row[measureId];
                    }
                    return acc + (typeof value === 'number' ? value : 0);
                }, 0);
                totals[measureId] = sum;
            } else {
                // For non-summable measures (percentages, trends, dates, text, etc.), set to null
                totals[measureId] = null;
            }
        });
        
        return totals;
    };

    const totals = calculateTotals();

	return (
		<Fragment>
			<Tooltip />
			<div className="w-full flex flex-col">
			<Table columns={columnDefs} data={data} showTotalRow={showTotalRow} totals={totals} measureColumns={measureColumns} selectedMeasureIds={selectedMeasureIds} ALL_MEASURES={ALL_MEASURES} onRowClick={onRowClick} isInsightTable={isInsightTable} />
			</div>
		</Fragment>
	);    
};

export default AnalyzerTable;



function Table({ columns, data, showTotalRow = false, totals = null, measureColumns = [], selectedMeasureIds = [], ALL_MEASURES = {}, onRowClick, isInsightTable = false }) {
	const defaultColumn = React.useMemo(() => ({ 
		width: 200,
		minWidth: 0,
		maxWidth: 200
	}), []);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		initialState,
	} = useTable({ columns, data, defaultColumn }, useFlexLayout);

	initialState.hiddenColumns = ["cost_allocation_id", "cost_allocation_id"];

	return (
		<div className="w-full flex flex-col">
			<div {...getTableProps()} className="w-full cursor-default flex flex-col">
				{/* Fixed Header */}
				<div className="flex-shrink-0 py-2 border-b border-border pl-4 text-text-2 w-full bg-card z-10 table-widget-header">
						{headerGroups.map((headerGroup) => {
							const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
							return (
							<div key={key} {...headerGroupProps} className="tr flex" style={{ minWidth: 0 }}>
								{headerGroup.headers.map((column, colIndex) => {
									const { key: columnKey, ...columnProps } = column.getHeaderProps();
									const isLastColumn = colIndex === headerGroup.headers.length - 1;
									const isGroupingColumn = column.id === 'grouping';
									const isFirstColumn = colIndex === 0;
									const columnWidth = column.maxWidth || column.width;
									
									// First column (grouping column or first measure in insight table): flexible, takes remaining space
									// Other measure columns: fixed width, pushed to the right
									const cellStyle = (isGroupingColumn || (isInsightTable && isFirstColumn)) ? {
										...columnProps.style,
										marginRight: '10px',
										minWidth: column.minWidth ? `${column.minWidth}px` : '200px',
										flexGrow: 1,
										flexShrink: 1,
										flexBasis: 0,
										overflow: 'hidden'
									} : {
										...columnProps.style,
										marginRight: isLastColumn ? 0 : '10px',
										width: columnWidth ? `${columnWidth}px` : undefined,
										maxWidth: columnWidth ? `${columnWidth}px` : undefined,
										minWidth: column.minWidth ? `${column.minWidth}px` : 0,
										flexShrink: 0,
										flexGrow: 0,
										overflow: 'hidden'
									};
									
									return (
										<div
											key={columnKey}
											{...columnProps}
											className="th uppercase text-sm text-text"
											style={cellStyle}
										>
											{column.render("Header")}
										</div>
									);
								})}
								</div>
							);
						})}
					</div>

				{/* Scrollable Body */}
				<div {...getTableBodyProps()} className="overflow-x-hidden table-widget-body">
						{rows.map((row, index) => {
							prepareRow(row);
							const { key: rowKey, ...rowProps } = row.getRowProps({});
							return (
							<div
								key={rowKey}
								{...rowProps}
								className={`tr flex border-b border-dashed border-border ${onRowClick ? 'cursor-pointer hover:bg-primary-soft' : ''}`}
								onClick={() => onRowClick && onRowClick(row.original)}
								style={{ minWidth: 0 }}
							>
									{row.cells.map((cell, cellIndex) => {
										const { key: cellKey, ...cellProps } = cell.getCellProps();
										const isLastCell = cellIndex === row.cells.length - 1;
										const isGroupingColumn = cell.column.id === 'grouping';
										const isFirstColumn = cellIndex === 0;
										const columnWidth = cell.column.maxWidth || cell.column.width;
										
										// First column (grouping column or first measure in insight table): flexible, takes remaining space
										// Other measure columns: fixed width, pushed to the right
										const cellStyle = (isGroupingColumn || (isInsightTable && isFirstColumn)) ? {
											...cellProps.style,
											marginRight: '10px',
											minWidth: cell.column.minWidth ? `${cell.column.minWidth}px` : '200px',
											flexGrow: 1,
											flexShrink: 1,
											flexBasis: 0,
											overflow: 'hidden'
										} : {
											...cellProps.style,
											marginRight: isLastCell ? 0 : '10px',
											width: columnWidth ? `${columnWidth}px` : undefined,
											maxWidth: columnWidth ? `${columnWidth}px` : undefined,
											minWidth: cell.column.minWidth ? `${cell.column.minWidth}px` : 0,
											flexShrink: 0,
											flexGrow: 0,
											overflow: 'hidden'
										};
										
										return (
											<div
												key={cellKey}
												{...cellProps}
												className="td truncate pl-4 pt-2 pb-1 text-sm"
												style={cellStyle}
											>
												{cell.render("Cell")}
											</div>
										);
									})}
								</div>
							);
						})}
						{/* Total Row */}
						{showTotalRow && totals && headerGroups.length > 0 && (
							<div className="tr flex border-b-2 border-solid border-border bg-surface-2">
							{headerGroups[0].headers.map((column, colIndex) => {
								const { key: columnKey, style } = column.getHeaderProps();
								const columnId = column.id;
								const isLastColumn = colIndex === headerGroups[0].headers.length - 1;
								const isGroupingColumn = columnId === 'grouping';
								const isFirstColumn = colIndex === 0;
								const columnWidth = column.maxWidth || column.width;
								
								// First column (grouping column or first measure in insight table): flexible, takes remaining space
								// Other measure columns: fixed width, pushed to the right
								const cellStyle = (isGroupingColumn || (isInsightTable && isFirstColumn)) ? {
									...style,
									marginRight: '10px',
									minWidth: column.minWidth ? `${column.minWidth}px` : '200px',
									flexGrow: 1,
									flexShrink: 1,
									flexBasis: 0,
									overflow: 'hidden'
								} : {
									...style,
									marginRight: isLastColumn ? 0 : '10px',
									width: columnWidth ? `${columnWidth}px` : undefined,
									maxWidth: columnWidth ? `${columnWidth}px` : undefined,
									minWidth: column.minWidth ? `${column.minWidth}px` : 0,
									flexShrink: 0,
									flexGrow: 0,
									overflow: 'hidden'
								};
								
								// For grouping column
								if (columnId === 'grouping') {
									return (
										<div
											key={columnKey}
											style={cellStyle}
											className="td truncate pl-4 pt-2 pb-1 text-sm font-semibold text-text"
										>
											Total
										</div>
									);
								}
									
								// For measure columns
								const measure = ALL_MEASURES[columnId];
								if (!measure) {
									// Render empty placeholder to maintain flex layout alignment
									return (
										<div
											key={columnKey}
											style={cellStyle}
											className="td truncate pl-4 pt-2 pb-1 text-sm font-semibold text-right"
											aria-hidden="true"
										>
										</div>
									);
								}
								
								const totalValue = totals[columnId];
								const isLeftAligned = measure.className && measure.className.includes("text-left");
								const alignmentClass = isLeftAligned ? "text-left" : "text-right";
									
                    // Fields that should be empty in total row (dates, text fields, etc.)
                    const emptyInTotalRow = [
                        'change_date',
                        'vendor_name',
                        'resource_cluster',
                        'resource_type',
                        'resource_status',
                        'resource_name',
                        // Resource list table fields (text/icon fields)
                        'vendor',
                        'type',
                        'status',
                        // Insight table fields (text fields)
                        'insight',
                        'insight_group'
                    ];
									
									return (
										<div
											key={columnKey}
											style={cellStyle}
											className={`td truncate pl-4 pt-2 pb-1 text-sm font-semibold ${alignmentClass} ${measure.className || ''}`}
										>
											{totalValue !== null ? (
												measure.cell({ value: totalValue, row: { original: {} } })
											) : emptyInTotalRow.includes(columnId) ? (
												<span></span>
											) : (
												<span className="text-text-2">-</span>
											)}
										</div>
									);
								})}
							</div>
						)}
				</div>
			</div>
		</div>
	);
}